import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';

const SensorTimeSeries = ({ 
  data, 
  sensorId = 'm1',  // 'm1' or 'm2' reading type
  selectedSensors = [], // array of sensor IDs to display
  sensorDetails = {}, // details about sensors including placements
  threshold = null,
  washDates = [],
  washTypes = {}, // Map of dates to wash types
  height = 400,
  smooth = true,
  showWashLine = false
}) => {
  // Date formatter for x-axis with validation
  const formatXAxis = (timestamp) => {
    try {
      // Skip invalid timestamps
      if (!timestamp || typeof timestamp !== 'string') {
        return '';
      }
      
      const dateObj = new Date(timestamp);
      
      // Check if the date is valid before formatting
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      
      return format(dateObj, 'MM/dd');
    } catch (error) {
      return '';
    }
  };

  // Date formatter for tooltip with validation
  const formatTooltipDate = (timestamp) => {
    try {
      // Skip invalid timestamps
      if (!timestamp || typeof timestamp !== 'string') {
        return 'Invalid date';
      }
      
      // Create a date without assuming UTC
      const dateObj = new Date(timestamp);
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }
      
      // Use local time formatting
      return format(dateObj, 'MM/dd/yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Position-based colors for individual sensors
  const positionColors = {
    low: '#4caf50',    // green
    middle: '#2196f3', // blue
    roof: '#f44336',   // red
    default: '#757575' // default gray
  };
  
  // Wash type colors
  const washTypeColors = {
    'Helvask': '#8bc34a',      // light green
    'Halvvask': '#03a9f4',     // light blue
    'Teknisk vask': '#ff9800', // orange
    'default': '#4caf50'       // fallback green
  };

  // Data cleaning hook to filter out invalid data points
  const sanitizedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Filter out data points with NaN or undefined values for the main readings
    return data.filter(item => {
      // If we're showing overall metrics, ensure m1/m2/avg have valid values
      if (selectedSensors.length === 0) {
        if (sensorId === 'm1') {
          return item.m1 !== undefined && item.m1 !== null && !isNaN(item.m1);
        } else if (sensorId === 'm2') {
          return item.m2 !== undefined && item.m2 !== null && !isNaN(item.m2);
        } else if (sensorId === 'avg') {
          return item.avg !== undefined && item.avg !== null && !isNaN(item.avg);
        }
      }
      // Otherwise, validate timestamp at minimum
      return item.timestamp && typeof item.timestamp === 'string';
    });
  }, [data, selectedSensors, sensorId]);
  
  // Data with sanitized values is used instead of raw data
  const dataForChart = sanitizedData.length > 0 ? sanitizedData : [];
  
  // Use sanitized data for the chart - this is the critical function to fix NaN errors
  // The core issue is that we need to validate each point before using it in the chart
  const filteredDataForChart = React.useMemo(() => {
    if (!dataForChart || dataForChart.length === 0) return [];
    
    return dataForChart.filter(point => {
      // Critical check: ensure the timestamp is valid for XAxis
      if (!point.timestamp || typeof point.timestamp !== 'string') return false;
      
      try {
        const testDate = new Date(point.timestamp);
        if (isNaN(testDate.getTime())) return false;
        
        // Also verify data values are valid numbers - this is essential to prevent NaN in SVG attributes
        if (selectedSensors.length === 0) {
          // If showing aggregate, validate that specific reading type
          return point[sensorId] !== undefined && point[sensorId] !== null && !isNaN(point[sensorId]);
        } else {
          // If showing individual sensors, at least ensure the timestamp is valid
          return true;
        }
      } catch (e) {
        return false;
      }
    });
  }, [dataForChart, sensorId, selectedSensors]);
  
  // Add wash status to data with wash type information
  const dataWithWashStatus = React.useMemo(() => {
    if (!dataForChart || dataForChart.length === 0) return [];
    
    // Create a deep copy of the sanitized data
    const enrichedData = dataForChart.map(item => ({...item}));
    
    // Initialize wash status fields for all wash types
    const washTypeKeys = Object.keys(washTypeColors);
    
    // Add wash status fields for each wash type (0 = no wash, 1 = wash of this type on this day)
    if (washDates && washDates.length > 0) {
      // For each data point, check if its date matches a wash date
      enrichedData.forEach(item => {
        if (!item.timestamp) return;
        
        // Extra validation for timestamp
        let itemDateStr;
        try {
          const itemDate = new Date(item.timestamp);
          if (isNaN(itemDate.getTime())) return; // Skip invalid dates
          itemDateStr = itemDate.toISOString().split('T')[0]; // Get YYYY-MM-DD
        } catch (e) {
          // Skip this item if any error occurs during timestamp processing
          return;
        }
        
        // Initialize all wash type statuses to 0
        item.washStatus = 0; // Default wash status
        washTypeKeys.forEach(type => {
          item[`washStatus_${type}`] = 0;
        });
        
        // Check if this date matches any wash date
        for (const washDate of washDates) {
          if (!washDate) continue;
          // Extract just the date part (YYYY-MM-DD) from the wash date
          const washDateStr = washDate.split('T')[0];
          
          if (washDateStr === itemDateStr) {
            // Get the wash type for this date
            const washType = washTypes[washDate] || 'default';
            
            // Set the general wash status
            item.washStatus = 1;
            
            // Set the specific wash type status
            if (washTypeKeys.includes(washType)) {
              item[`washStatus_${washType}`] = 1;
            } else {
              item.washStatus_default = 1;
            }
            
            // Break since we found a match
            break;
          }
        }
      });
    }
    
    return enrichedData;
  }, [dataForChart, washDates, washTypes]);
  
  // Data stats hook - kept but logs removed to avoid console spam
  React.useEffect(() => {
    // This hook is kept but logging is removed to prevent console flooding
  }, [data, selectedSensors, sensorId]);
  
  // Process wash reference lines with useMemo to avoid recalculations
  const washReferenceLines = React.useMemo(() => {
    if (!washDates || washDates.length === 0 || dataForChart.length === 0) return [];
    
    // First, extract just the date part from each wash date to deduplicate them
    const uniqueDatesMap = new Map(); // Map of YYYY-MM-DD to {date, type}
    
    // Process each wash date to ensure uniqueness by date
    washDates.forEach(date => {
      if (!date) return;
      
      // Extract just the date part (YYYY-MM-DD)
      let dateOnly;
      try {
        // Make sure we're dealing with a valid date string
        if (typeof date !== 'string') {
          return;
        }
        
        dateOnly = date.split('T')[0]; // First try to split at 'T'
        if (!dateOnly) {
          // If no 'T', use the whole date string as is
          dateOnly = date;
        }
        
        // Validate that dateOnly produces a valid date
        const testDate = new Date(dateOnly);
        if (isNaN(testDate.getTime())) {
          return; // Skip invalid dates
        }
      } catch (e) {
        return; // Skip problematic entries entirely
      }
      
      // Store with the wash type (use the first occurrence if we have duplicates)
      if (dateOnly && !uniqueDatesMap.has(dateOnly)) {
        uniqueDatesMap.set(dateOnly, {
          date: date,
          type: washTypes[date] || 'default'
        });
      }
    });
    
    // Now render one reference line per unique date
    return Array.from(uniqueDatesMap.entries())
      .filter(([dateOnly]) => dateOnly && dateOnly !== 'undefined' && dateOnly !== 'null')
      .map(([dateOnly, {date, type}], index) => {
      // Format the date for display in a shorter format
      let displayDate;
      try {
        const dateObj = new Date(dateOnly);
        displayDate = format(dateObj, 'MM/dd');
      } catch (e) {
        displayDate = 'Wash';
      }
      
      // Get color based on wash type
      const color = washTypeColors[type] || washTypeColors['default'];
      
      // Determine label based on wash type
      let label = `Wash ${displayDate}`;
      if (type !== 'default') {
        // Shorten the label for space
        const typeLabel = type === 'Teknisk vask' ? 'Teknisk' : 
                        (type === 'Halvvask' ? 'Halv' : 
                         type === 'Helvask' ? 'Hel' : type);
        label = `${typeLabel} ${displayDate}`;
      }
      
      // Create a timestamp at 08:00 for this date to match data format
      // IMPORTANT: Must match exact format in data points to avoid NaN coordinate errors
      const timestamp = `${dateOnly}T08:00:00.000`;
      
      // Validate the timestamp creates a valid date - skip if invalid
      try {
        const testDate = new Date(timestamp);
        if (isNaN(testDate.getTime())) {
          console.warn(`Invalid reference line timestamp: ${timestamp}`);
          return null; // Skip this reference line completely
        }
        
        // Check that this wash date timestamp falls within our data range
        // This prevents coordinates going outside the chart domain causing NaN errors
        if (dataForChart.length > 0) {
          const firstDataDate = new Date(dataForChart[0].timestamp);
          const lastDataDate = new Date(dataForChart[dataForChart.length - 1].timestamp);
          
          // If the wash timestamp is well outside our data range, skip it
          if (testDate < firstDataDate && (firstDataDate - testDate) > 30 * 24 * 60 * 60 * 1000) {
            console.warn(`Wash date ${timestamp} is before chart data range`);
            return null;
          }
          if (testDate > lastDataDate && (testDate - lastDataDate) > 30 * 24 * 60 * 60 * 1000) {
            console.warn(`Wash date ${timestamp} is after chart data range`);
            return null;
          }
        }
      } catch (e) {
        console.warn(`Error creating reference line date: ${timestamp}`);
        return null; // Skip on any error
      }
      
      // Return the reference line, but with safer error handling
      try {
        return (
          <ReferenceLine
            key={`wash-${dateOnly}`}
            x={timestamp}
            stroke={color} 
            strokeDasharray="3 3"
            label={{ 
              value: label, 
              position: 'top',
              fill: color,
              fontSize: 10
            }}
            // Safer to use "discard" instead of "extendDomain" to prevent NaN errors
            ifOverflow="discard"
            isFront={true}
          />
        );
      } catch (error) {
        console.warn(`Failed to render wash reference line for ${timestamp}`);
        return null;
      }
    });
  }, [washDates, washTypes, washTypeColors, dataForChart]);

  // Ensure washReferenceLines is always an array, even if there's an error
  const safeWashReferenceLines = Array.isArray(washReferenceLines) ? 
    washReferenceLines.filter(line => line !== null) : [];
    
  // Verify we have valid data - IMPORTANT: After hooks to avoid rule violations
  if (!dataForChart || !Array.isArray(dataForChart) || dataForChart.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: height,
        bgcolor: '#f5f5f5',
        borderRadius: 1
      }}>
        <Typography variant="subtitle1">No valid data available for display</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ width: '100%', height: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={dataWithWashStatus}
          margin={{ top: 15, right: 30, left: 20, bottom: 15 }}
          syncId="sensorChartGroup" // Sync charts with the same ID for better responsiveness
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp" 
            tickFormatter={formatXAxis}
            // Handle invalid timestamps by using a custom tick value
            tickCount={10}
            allowDataOverflow={true}
          />
          <YAxis 
            allowDecimals={false}
            domain={['auto', 'auto']}
            allowDataOverflow={true}
          />
          {showWashLine && (
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              domain={[0, 1]}
              ticks={[0, 1]}
              tickFormatter={(value) => value === 1 ? 'Wash' : 'No Wash'}
              hide={true} // Hide the axis but keep the functionality
            />
          )}
          <Tooltip labelFormatter={formatTooltipDate} />
          <Legend />
          
          {/* Overall line (when no individual sensors are selected) */}
          {selectedSensors.length === 0 && (
            <Line
              key={`overall-${sensorId}`} // Force line to update when sensorId changes
              type={smooth ? "monotone" : "linear"} // Keep the smooth toggle functionality
              dataKey={sensorId}
              name={`${sensorId.toUpperCase()} (Overall)`}
              stroke="#1976d2"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
              connectNulls={true}
              isAnimationActive={false} // Disable animation to reduce render issues
              // Added these critical attributes to better handle NaN values
              hide={dataWithWashStatus.length === 0 || !dataWithWashStatus.some(item => 
                item[sensorId] !== undefined && 
                item[sensorId] !== null && 
                !isNaN(item[sensorId])
              )}
            />
          )}
          
          {/* Individual sensor lines when sensors are selected */}
          {selectedSensors.map((sensorCode) => {
            // Get placement info for this sensor
            const placement = sensorDetails.placements?.[sensorCode] || {};
            const position = placement.position || 'default';
            
            // Get position-based color
            const color = positionColors[position];
            
            // First check if we have data with a special formatted key that includes reading type
            // Format: sensorId_m1 or sensorId_m2
            const specialKey = `${sensorCode}_${sensorId}`;
            const hasSpecialKeyData = data.some(item => 
              item[specialKey] !== undefined && 
              item[specialKey] !== null
            );
            
            // If we have special key data, use that
            if (hasSpecialKeyData) {
              
              // Check if any valid data exists for this sensor
              const hasValidData = dataWithWashStatus.some(item => 
                item[specialKey] !== undefined && 
                item[specialKey] !== null && 
                !isNaN(item[specialKey])
              );
              
              if (!hasValidData) return null;
              
              return (
                <Line
                  key={`${sensorCode}-${sensorId}`} // Include reading type to force update
                  type={smooth ? "monotone" : "linear"} // Keep the smooth toggle functionality
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={true}
                  dataKey={specialKey} // Use the special formatted key
                  name={`${sensorCode}${position ? ` (${position})` : ''}`}
                  stroke={color}
                  isAnimationActive={false} // Disable animation to reduce render issues
                />
              );
            }
            
            // If no special key, check if sensor exists in data directly with plain sensorId key
            const hasSensorData = dataWithWashStatus.some(item => 
              item[sensorCode] !== undefined && 
              item[sensorCode] !== null && 
              !isNaN(item[sensorCode])
            );
            
            if (!hasSensorData) {
              return null;
            }
            
            // Check if any valid data exists for this sensor
            const hasValidData = dataWithWashStatus.some(item => 
              item[sensorCode] !== undefined && 
              item[sensorCode] !== null && 
              !isNaN(item[sensorCode])
            );
            
            if (!hasValidData) return null;
            
            return (
              <Line
                key={`${sensorCode}-${sensorId}`} // Include reading type to force update
                type={smooth ? "monotone" : "linear"} // Keep the smooth toggle functionality
                dataKey={sensorCode}
                name={`${sensorCode}${position ? ` (${position})` : ''}`}
                stroke={color}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls={true}
                isAnimationActive={false} // Disable animation to reduce render issues
              />
            );
          })}
          
          {/* Threshold line if provided */}
          {threshold !== null && (
            <ReferenceLine
              y={threshold}
              stroke="#f44336"
              strokeDasharray="3 3"
              label={{ 
                value: `Threshold: ${threshold}`, 
                position: 'right',
                fill: '#f44336'
              }}
            />
          )}
          
          {/* Wash lines showing different types of wash events */}
          {showWashLine && dataWithWashStatus.length > 0 && (
            <>
              {/* Add a line for each type of wash */}
              {Object.keys(washTypeColors).map(washType => {
                // Check if we have data for this wash type
                const hasWashData = dataWithWashStatus.some(item => 
                  item[`washStatus_${washType}`] !== undefined && 
                  !isNaN(item[`washStatus_${washType}`])
                );
                
                if (!hasWashData) return null;
                
                return (
                  <Line
                    key={`wash-line-${washType}`}
                    type="step" // Step is more stable than stepAfter
                    dataKey={`washStatus_${washType}`}
                    name={washType === 'default' ? 'Wash Events' : washType}
                    stroke={washTypeColors[washType]}
                    strokeWidth={2}
                    // Remove dots completely to avoid positioning errors
                    dot={false}
                    activeDot={false}
                    connectNulls={true}
                    yAxisId="right"
                    // Explicitly set domain to prevent calculations that might result in NaN
                    domain={[0, 1]}
                    isAnimationActive={false} // Disable animation to reduce render issues
                  />
                );
              })}
            </>
          )}
          
          {/* Wash event markers (vertical lines) - use safe filtered array */}
          {safeWashReferenceLines}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default SensorTimeSeries;